import { useRef, useState } from 'react';
import {
  Button,
  Card,
  Dialog,
  Icon,
  Typography,
} from '@equinor/eds-core-react';
import {
  error_outlined as errorOutlined,
  upload as uploadIcon,
} from '@equinor/eds-icons';
import * as Styled from './ExternalSimulationUpload.styled';
import {
  UploadStep,
  useExternalSimulationUpload,
} from './hooks/useExternalSimulationUpload';

const stepLabel: Record<UploadStep, string> = {
  idle: '',
  initiating: 'Initiating upload…',
  uploading: 'Uploading to storage…',
  completing: 'Processing file…',
  done: 'Upload complete!',
  error: 'Upload failed',
};

export const ExternalSimulationUpload = () => {
  const [open, setOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { step, progress, error, upload, cancel, reset } =
    useExternalSimulationUpload();

  const isUploading =
    step === 'initiating' || step === 'uploading' || step === 'completing';

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    if (isUploading) return;
    setOpen(false);
    setSelectedFile(null);
    reset();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleUpload = () => {
    if (selectedFile) {
      upload(selectedFile);
    }
  };

  const handleCancel = () => {
    cancel();
    setSelectedFile(null);
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <>
      <Card className="external-files-card">
        <Card.Header>
          <Typography variant="h4" as="h3" className="external-files-title">
            External simulation upload
          </Typography>
        </Card.Header>
        <Card.Content>
          <Typography variant="body_short">
            Upload a ZIP file containing Delft3d-GT simulation results directly
            to PEPM.
          </Typography>
        </Card.Content>
        <Card.Actions>
          <Button variant="outlined" onClick={handleOpen}>
            <Icon data={uploadIcon} />
            Upload external simulation
          </Button>
        </Card.Actions>
      </Card>

      <Styled.UploadDialog open={open}>
        <Dialog.Header>Upload external simulation</Dialog.Header>
        <Dialog.Content>
          <Styled.Content>
            {step === 'idle' && (
              <>
                <Typography variant="body_long">
                  Select a ZIP file containing external simulation results to
                  upload directly to cloud storage.
                </Typography>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                />

                {selectedFile ? (
                  <Styled.FileInfo>
                    <Typography variant="body_short">
                      {selectedFile.name} ({formatSize(selectedFile.size)})
                    </Typography>
                  </Styled.FileInfo>
                ) : (
                  <Button
                    variant="outlined"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Select ZIP file
                  </Button>
                )}
              </>
            )}

            {isUploading && (
              <Styled.ProgressSection>
                <Typography variant="body_short">{stepLabel[step]}</Typography>
                <Styled.StyledProgress
                  variant={
                    step === 'uploading' ? 'determinate' : 'indeterminate'
                  }
                  value={step === 'uploading' ? progress : undefined}
                />
                {step === 'uploading' && (
                  <Typography variant="caption">{progress}%</Typography>
                )}
              </Styled.ProgressSection>
            )}

            {step === 'done' && (
              <Typography variant="body_short" color="success">
                ✓ {stepLabel.done}
              </Typography>
            )}

            {step === 'error' && (
              <Styled.FileInfo>
                <Icon data={errorOutlined} color="danger" />
                <Typography variant="body_short">{error}</Typography>
              </Styled.FileInfo>
            )}
          </Styled.Content>
        </Dialog.Content>

        <Styled.Actions>
          {step === 'idle' && (
            <>
              <Button variant="outlined" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={!selectedFile}>
                Upload
              </Button>
            </>
          )}

          {isUploading && (
            <Button variant="outlined" color="danger" onClick={handleCancel}>
              Cancel upload
            </Button>
          )}

          {step === 'done' && <Button onClick={handleClose}>Close</Button>}

          {step === 'error' && (
            <>
              <Button variant="outlined" onClick={handleClose}>
                Close
              </Button>
              <Button
                onClick={() => {
                  reset();
                  setSelectedFile(null);
                }}
              >
                Try again
              </Button>
            </>
          )}
        </Styled.Actions>
      </Styled.UploadDialog>
    </>
  );
};
